<script setup lang="ts">
import { DeleteIcon, FilterDismissIcon, SparkleIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { NxButton } from '../Button';
import { NxDialog } from '../Dialog';
import { i18nInjectionKey } from '../internal';
import type { NxSavedTableFilter } from './filterModel';

/**
 * Filter-management modal, opened from the header gear → „Filter verwalten…“:
 * saved filter sets and the filter wizard entry. Search-row visibility lives
 * in NxTablePreferencesDialog („Einstellungen…“).
 */
const isOpen = defineModel<boolean>('open', { required: true });

const props = defineProps<{
  hasActiveFilters: boolean;
  savedFilters: NxSavedTableFilter[];
}>();

const emit = defineEmits<{
  'open-wizard': [];
  'save-current': [];
  'reset-filters': [];
  'apply-saved': [id: string];
  'delete-saved': [id: string];
}>();

const i18n = inject(i18nInjectionKey)!;

const hasSavedFilters = computed(() => props.savedFilters.length > 0);
</script>

<template>
  <NxDialog
    v-model:open="isOpen"
    :title="i18n.t('common.table.filter.manage.title')"
    :description="i18n.t('common.table.filter.manage.description')"
    size="wide"
    fill
  >
    <template #content>
      <div class="settings-body">
        <section class="settings-section">
          <div class="action-row">
            <NxButton variant="outline" height="s" @click="emit('open-wizard')">
              <SparkleIcon />
              {{ i18n.t('common.table.filter.manage.open_wizard') }}
            </NxButton>
            <NxButton
              variant="outline"
              height="s"
              :disabled="!props.hasActiveFilters"
              @click="emit('save-current')"
            >
              {{ i18n.t('common.table.filter.save_current') }}
            </NxButton>
            <NxButton
              variant="outline"
              height="s"
              :disabled="!props.hasActiveFilters"
              @click="emit('reset-filters')"
            >
              <FilterDismissIcon />
              {{ i18n.t('common.table.filter.reset_all') }}
            </NxButton>
          </div>

          <template v-if="hasSavedFilters">
            <h4 class="subsection-title">{{ i18n.t('common.table.filter.saved.title') }}</h4>
            <ul class="saved-list">
              <li v-for="saved in props.savedFilters" :key="saved.id" class="saved-row">
                <span class="saved-name">{{ saved.name }}</span>
                <NxButton height="s" variant="outline" @click="emit('apply-saved', saved.id)">
                  {{ i18n.t('common.table.filter.saved.apply') }}
                </NxButton>
                <button
                  type="button"
                  class="icon-action"
                  :title="i18n.t('common.table.filter.saved.delete')"
                  @click="emit('delete-saved', saved.id)"
                >
                  <DeleteIcon />
                </button>
              </li>
            </ul>
          </template>
          <p v-else class="saved-empty">{{ i18n.t('common.table.filter.saved.empty') }}</p>
        </section>
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton @click="close">
        {{ i18n.t('common.table.filter.manage.close') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.settings-body {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  margin-top: var(--nxt-space-2xs);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-md);
}

.settings-section {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.subsection-title {
  margin: var(--nxt-space-2xs) 0 0;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
}

.saved-list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* The global type reset sizes every bare <ul>/<p> at 16px medium. */
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}

.saved-row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.saved-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
}

.saved-empty {
  margin: 0;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}

.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--nxt-control-size-sm);
  height: var(--nxt-control-size-sm);
  padding: 0;
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-muted);
  cursor: pointer;

  &:hover {
    background-color: var(--nxt-brand-subtle-bg);
    color: var(--nxt-fg);
  }
}
</style>
